import type { Context } from "../context.js";
import { SESSION_ACTIVE_KEY } from "../context.js";
import type { EmitFn } from "../adapters.js";

/**
 * Tiempo en página, lectura activa (descontando idle y pestaña oculta) e idle.
 *
 * Acumulamos por TIEMPO REAL transcurrido (`delta = now - lastTick`), no por
 * "1s por tick": bajo jank del hilo principal (ads del publisher) o throttling de
 * timers en segundo plano, un tick puede representar varios segundos, y contar
 * "1s por tick" subcontaría sistemáticamente. El crédito activo se capa al umbral
 * de inactividad (un hueco largo por throttling/suspensión no es lectura activa
 * continua). `timeOnPageMs` es reloj de pared desde la carga (incluye segundo
 * plano); `activeReadingMs` solo suma con pestaña visible y actividad reciente.
 *
 * `settle()` aplica el crédito pendiente hasta el instante actual; se llama desde
 * el timer de 1s, al cambiar de visibilidad, y bajo demanda (heartbeats / salida)
 * para no perder el último tramo parcial. Es idempotente (un segundo settle
 * seguido suma ~0).
 */
export function startIdle(ctx: Context, emit: EmitFn, idleThresholdMs: number) {
  let lastActivity = Date.now();
  let lastTick = Date.now();
  let isIdle = false;
  let visible = !document.hidden;

  // Suscriptores del tick de tiempo ACTIVO. Reciben los ms que se acaban de
  // acreditar a activeReadingMs en este settle (ya capados al umbral y gateados por
  // visible+activo). El colector de tiempo-por-sección reparte EXACTAMENTE ese
  // crédito entre las franjas visibles → un solo reloj de pared, sin doble conteo,
  // y Σ(tiempo por sección) ≤ activeReadingMs por construcción. Un colector que
  // lance NUNCA puede tumbar el reloj (ni la página del publisher).
  const tickSubs: Array<(creditedMs: number) => void> = [];

  function bumpActivity() {
    lastActivity = Date.now();
    if (isIdle) {
      isIdle = false;
      ctx.state.idleMs = 0;
      emit("active", {});
      ctx.touch();
    }
  }

  ["click", "keydown", "scroll", "mousemove", "touchstart"].forEach((ev) =>
    window.addEventListener(ev, bumpActivity, { passive: true })
  );

  /** Credita el tiempo real desde el último settle según el estado actual. */
  function settle() {
    const now = Date.now();
    const delta = now - lastTick;
    lastTick = now;
    if (delta <= 0) return;
    ctx.state.timeOnPageMs += delta; // reloj de pared (incluye segundo plano)
    const idleFor = now - lastActivity;
    if (visible && idleFor < idleThresholdMs) {
      // Activo: nunca acreditamos más que el umbral de un solo tramo (evita que un
      // hueco largo por throttling o suspensión cuente como lectura continua).
      const credit = Math.min(delta, idleThresholdMs);
      ctx.state.activeReadingMs += credit; // por página
      ctx.state.sessionActiveMs += credit; // acumulado de sesión (encadena entre cargas)
      try {
        sessionStorage.setItem(SESSION_ACTIVE_KEY, String(ctx.state.sessionActiveMs));
      } catch {
        /* incógnito/cuota: la métrica de sesión degrada a por-carga, nunca rompe */
      }
      for (const cb of tickSubs) {
        try {
          cb(credit);
        } catch {
          /* un suscriptor (p. ej. tiempo-por-sección) jamás tumba el reloj */
        }
      }
    }
    ctx.state.idleMs = idleFor;
    if (!isIdle && idleFor >= idleThresholdMs) {
      isIdle = true;
      emit("idle", { ms: idleFor });
    }
    ctx.touch();
  }

  document.addEventListener(
    "visibilitychange",
    () => {
      // Settle ANTES de cambiar `visible`: así el último tramo se acredita con el
      // estado de visibilidad que tenía hasta este instante.
      settle();
      visible = !document.hidden;
      emit("tab_visibility", { hidden: document.hidden });
      if (visible) {
        lastTick = Date.now(); // no acreditar el hueco oculto al volver
        bumpActivity();
      }
    },
    { passive: true }
  );

  setInterval(settle, 1000);

  return {
    settle,
    /** Suscribe un callback al tick de tiempo activo. Recibe los ms acreditados en
     *  cada settle con la pestaña visible y el usuario presente (0 nunca se notifica). */
    onTick: (cb: (creditedMs: number) => void) => {
      tickSubs.push(cb);
    },
  };
}
